/**
 * The Dashboard's numbers, in one place.
 *
 * Every read below goes through the browser's anon-key client, so RLS scopes it
 * to `owner_id = auth.uid()` without a single explicit filter — the same
 * guarantee the rest of the app relies on. Nothing here queries a column that
 * does not exist on the tables 0001–0015 created.
 *
 * Aggregation is client-side over capped reads, matching the existing analytics
 * convention in db.ts: at admin-tool volumes it is instant, and it keeps the
 * database free of bespoke reporting views.
 *
 * Every rate guards its denominator — a fresh account has zero of everything,
 * and 0/0 must render as "0%", not NaN.
 */
import { supabase } from './supabase'
import { isArchived } from '@shared/archive'
import type { CampaignStats } from '@shared/types'

const ROW_CAP = 10000
/** The reporting window, and the length of the period we compare it against. */
export const WINDOW_DAYS = 14

export interface Bucket {
  /** YYYY-MM-DD, local */
  day: string
  /** e.g. "12 Mar" */
  label: string
  sent: number
  opens: number
}

export interface GaugeMetric {
  label: string
  /** 0–1 */
  value: number
  /** percentage points vs the previous window; null when there is no baseline */
  delta: number | null
  numerator: number
  denominator: number
  color: string
}

export interface SenderRow {
  key: string
  label: string
  sent: number
  opens: number
  openRate: number
}

export interface CampaignRow {
  id: string
  name: string
  status: string
  sent: number
  total: number
  openRate: number
  clickRate: number
}

export interface DashboardData {
  windowDays: number
  /** daily sends + opens across the window */
  series: Bucket[]
  sentTotal: number
  /** last 7 days vs the 7 before, as a fraction; null when the baseline is 0 */
  wowDelta: number | null
  gauges: GaugeMetric[]
  funnel: { label: string; value: number; pct: number; color: string }[]
  leadsByCampaign: { name: string; leads: number }[]
  leadsTotal: number
  topSenders: SenderRow[]
  queueByStatus: { status: string; count: number; color: string }[]
  queueTotal: number
  unsubs: { total: number; pctOfSent: number; series: { day: string; count: number }[] }
  opensByHour: { hour: number; label: string; opens: number }[]
  peakHours: number[]
  campaigns: CampaignRow[]
  /** true when the account has produced no sends, opens or leads at all */
  isEmpty: boolean
}

export const CHART = {
  indigo: '#5b5bd6',
  indigoLight: '#8b88ea',
  pink: '#ff77b6',
  amber: '#f0b429',
  green: '#22b07a',
  red: '#ef4444',
  blue: '#3b82f6',
  slate: '#a1a1aa',
}

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const dayLabel = (d: Date) =>
  d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })

/** 0 when the denominator is 0, so no rate can ever be NaN or Infinity. */
const rate = (n: number, d: number) => (d > 0 ? n / d : 0)

function startOfDayNDaysAgo(n: number): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - n)
  return d
}

interface QueueRow {
  id: string
  campaign_id: string | null
  status: string
  sent_at: string | null
  sent_from: string | null
  created_at: string
}
interface EventRow {
  queue_id: string | null
  campaign_id: string | null
  type: string
  at: string
}

export async function loadDashboard(): Promise<DashboardData> {
  const windowStart = startOfDayNDaysAgo(WINDOW_DAYS - 1)
  const priorStart = startOfDayNDaysAgo(WINDOW_DAYS * 2 - 1)

  // One round trip for everything. Events and unsubscribes are fetched over the
  // DOUBLE window so the "vs previous period" deltas have a baseline.
  const [queueRes, eventRes, unsubRes, leadRes, campaignRes, statsRes, identityRes] =
    await Promise.all([
      supabase
        .from('send_queue')
        .select('id, campaign_id, status, sent_at, sent_from, created_at')
        .limit(ROW_CAP),
      supabase
        .from('email_events')
        .select('queue_id, campaign_id, type, at')
        .gte('at', priorStart.toISOString())
        .limit(ROW_CAP),
      supabase
        .from('unsubscribes')
        .select('campaign_id, at')
        .gte('at', priorStart.toISOString())
        .limit(ROW_CAP),
      supabase.from('leads').select('id, campaign_id').limit(ROW_CAP),
      supabase.from('campaigns').select('id, name, status').limit(ROW_CAP),
      supabase.from('campaign_stats').select('*'),
      supabase.from('smtp_accounts_safe').select('id, label, from_email'),
    ])

  for (const r of [queueRes, eventRes, unsubRes, leadRes, campaignRes, statsRes]) {
    if (r.error) throw new Error(r.error.message)
  }
  // Identities read through a view that a pending migration can break; the rest
  // of the dashboard should still render if it does.
  const identities = (identityRes.data ?? []) as { id: string; label: string; from_email: string }[]

  const queue = (queueRes.data ?? []) as QueueRow[]
  const events = (eventRes.data ?? []) as EventRow[]
  const unsubRows = (unsubRes.data ?? []) as { campaign_id: string | null; at: string }[]
  const leads = (leadRes.data ?? []) as { id: string; campaign_id: string | null }[]
  const campaigns = (campaignRes.data ?? []) as { id: string; name: string; status: string }[]
  const stats = (statsRes.data ?? []) as CampaignStats[]

  const inWindow = (iso: string | null) => !!iso && new Date(iso) >= windowStart
  const inPrior = (iso: string | null) =>
    !!iso && new Date(iso) >= priorStart && new Date(iso) < windowStart

  // --- daily buckets --------------------------------------------------------
  const buckets = new Map<string, Bucket>()
  for (let i = WINDOW_DAYS - 1; i >= 0; i--) {
    const d = startOfDayNDaysAgo(i)
    buckets.set(dayKey(d), { day: dayKey(d), label: dayLabel(d), sent: 0, opens: 0 })
  }

  const sentRows = queue.filter((q) => q.status === 'sent' && q.sent_at)
  for (const q of sentRows) {
    const b = buckets.get(dayKey(new Date(q.sent_at!)))
    if (b) b.sent++
  }
  for (const e of events) {
    if (e.type !== 'open') continue
    const b = buckets.get(dayKey(new Date(e.at)))
    if (b) b.opens++
  }
  const series = [...buckets.values()]

  // --- hero + week-over-week ------------------------------------------------
  const sentTotal = series.reduce((s, b) => s + b.sent, 0)
  const lastWeek = series.slice(-7).reduce((s, b) => s + b.sent, 0)
  const prevWeek = series.slice(0, 7).reduce((s, b) => s + b.sent, 0)
  const wowDelta = prevWeek > 0 ? (lastWeek - prevWeek) / prevWeek : null

  // --- rates ----------------------------------------------------------------
  // Opens and clicks are counted per QUEUE ROW, not per event: one recipient
  // opening the same email six times is one open, or the rate exceeds 100%.
  const uniq = (pred: (e: EventRow) => boolean) => {
    const set = new Set<string>()
    for (const e of events) if (pred(e) && e.queue_id) set.add(e.queue_id)
    return set
  }
  const opensNow = uniq((e) => e.type === 'open' && inWindow(e.at))
  const clicksNow = uniq((e) => e.type === 'click' && inWindow(e.at))
  const opensPrev = uniq((e) => e.type === 'open' && inPrior(e.at))
  const clicksPrev = uniq((e) => e.type === 'click' && inPrior(e.at))

  const sentNow = sentRows.filter((q) => inWindow(q.sent_at)).length
  const sentPrev = sentRows.filter((q) => inPrior(q.sent_at)).length
  // A failed row has no sent_at, so its timestamp is created_at.
  const failedRows = queue.filter((q) => q.status === 'failed')
  const failedNow = failedRows.filter((q) => inWindow(q.created_at)).length
  const failedPrev = failedRows.filter((q) => inPrior(q.created_at)).length

  /** Percentage-point movement, or null when the previous window sent nothing. */
  const delta = (nNow: number, dNow: number, nPrev: number, dPrev: number) =>
    dPrev > 0 ? rate(nNow, dNow) - rate(nPrev, dPrev) : null

  const gauges: GaugeMetric[] = [
    {
      label: 'Open rate',
      value: rate(opensNow.size, sentNow),
      delta: delta(opensNow.size, sentNow, opensPrev.size, sentPrev),
      numerator: opensNow.size,
      denominator: sentNow,
      color: CHART.indigo,
    },
    {
      label: 'Click rate',
      value: rate(clicksNow.size, sentNow),
      delta: delta(clicksNow.size, sentNow, clicksPrev.size, sentPrev),
      numerator: clicksNow.size,
      denominator: sentNow,
      color: CHART.green,
    },
    {
      label: 'Bounce rate',
      value: rate(failedNow, sentNow),
      delta: delta(failedNow, sentNow, failedPrev, sentPrev),
      numerator: failedNow,
      denominator: sentNow,
      color: CHART.red,
    },
  ]

  // --- funnel (no "Replied": nothing tracks replies) ------------------------
  const funnel = [
    { label: 'Sent', value: sentNow, color: CHART.indigo },
    { label: 'Opened', value: opensNow.size, color: CHART.pink },
    { label: 'Clicked', value: clicksNow.size, color: CHART.amber },
  ].map((s) => ({ ...s, pct: rate(s.value, sentNow) }))

  // --- leads by campaign ----------------------------------------------------
  const nameById = new Map(campaigns.map((c) => [c.id, c.name]))
  const leadTally = new Map<string, number>()
  for (const l of leads) {
    const k = l.campaign_id ?? '—'
    leadTally.set(k, (leadTally.get(k) ?? 0) + 1)
  }
  const leadsByCampaign = [...leadTally.entries()]
    .map(([id, n]) => ({ name: nameById.get(id) ?? 'Unassigned', leads: n }))
    .sort((a, b) => b.leads - a.leads)
    .slice(0, 8)

  // --- top senders ----------------------------------------------------------
  // send_log carries no identity, but every sent queue row stamps `sent_from`
  // with the mailbox that delivered it — that IS the per-sender key.
  const identityLabel = new Map(identities.map((i) => [i.from_email, i.label]))
  const queueSender = new Map<string, string>()
  const senderSent = new Map<string, number>()
  for (const q of sentRows) {
    if (!q.sent_from) continue
    queueSender.set(q.id, q.sent_from)
    senderSent.set(q.sent_from, (senderSent.get(q.sent_from) ?? 0) + 1)
  }
  const senderOpens = new Map<string, Set<string>>()
  for (const e of events) {
    if (e.type !== 'open' || !e.queue_id) continue
    const from = queueSender.get(e.queue_id)
    if (!from) continue
    if (!senderOpens.has(from)) senderOpens.set(from, new Set())
    senderOpens.get(from)!.add(e.queue_id)
  }
  const topSenders: SenderRow[] = [...senderSent.entries()]
    .map(([from, sent]) => {
      const opens = senderOpens.get(from)?.size ?? 0
      return {
        key: from,
        label: identityLabel.get(from) ?? from,
        sent,
        opens,
        openRate: rate(opens, sent),
      }
    })
    .sort((a, b) => b.sent - a.sent)
    .slice(0, 5)

  // --- queue donut ----------------------------------------------------------
  const statusColor: Record<string, string> = {
    sent: CHART.green,
    scheduled: CHART.blue,
    failed: CHART.red,
    paused: CHART.amber,
    canceled: CHART.slate,
  }
  const statusTally = new Map<string, number>()
  for (const q of queue) statusTally.set(q.status, (statusTally.get(q.status) ?? 0) + 1)
  const queueByStatus = ['sent', 'scheduled', 'failed', 'paused', 'canceled']
    .map((s) => ({ status: s, count: statusTally.get(s) ?? 0, color: statusColor[s] }))
    .filter((s) => s.count > 0)
  const queueTotal = queue.length

  // --- unsubscribes ---------------------------------------------------------
  const unsubBuckets = new Map<string, number>()
  for (const b of buckets.keys()) unsubBuckets.set(b, 0)
  let unsubTotal = 0
  for (const u of unsubRows) {
    if (!inWindow(u.at)) continue
    unsubTotal++
    const k = dayKey(new Date(u.at))
    if (unsubBuckets.has(k)) unsubBuckets.set(k, unsubBuckets.get(k)! + 1)
  }
  const unsubs = {
    total: unsubTotal,
    pctOfSent: rate(unsubTotal, sentNow),
    series: [...unsubBuckets.entries()].map(([day, count]) => ({ day, count })),
  }

  // --- opens by hour of day -------------------------------------------------
  const hours = Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    label: `${String(h).padStart(2, '0')}`,
    opens: 0,
  }))
  for (const e of events) {
    if (e.type !== 'open' || !inWindow(e.at)) continue
    hours[new Date(e.at).getHours()].opens++
  }
  const maxHour = Math.max(...hours.map((h) => h.opens))
  const peakHours = maxHour > 0 ? hours.filter((h) => h.opens === maxHour).map((h) => h.hour) : []

  // --- current campaigns ----------------------------------------------------
  const statsById = new Map(stats.map((s) => [s.campaign_id, s]))
  const campaignOpens = new Map<string, Set<string>>()
  const campaignClicks = new Map<string, Set<string>>()
  for (const e of events) {
    if (!e.campaign_id || !e.queue_id) continue
    const target = e.type === 'open' ? campaignOpens : e.type === 'click' ? campaignClicks : null
    if (!target) continue
    if (!target.has(e.campaign_id)) target.set(e.campaign_id, new Set())
    target.get(e.campaign_id)!.add(e.queue_id)
  }
  // "Current campaigns" is a list of live work, so an archived one drops out of
  // it — but ONLY out of this list. Every number above (the series, the gauges,
  // the funnel, Top senders, leads by campaign) still counts its rows and still
  // uses its name, which is the entire reason archiving exists.
  //
  // The flag is read off campaign_stats, which is selected with `*` — so this
  // needs no new column in the campaigns read and works unchanged on a database
  // where 0021 is still pending (nothing is archived there).
  const campaignRows: CampaignRow[] = campaigns
    .filter((c) => !isArchived(statsById.get(c.id)))
    .filter((c) => c.status === 'active' || c.status === 'paused' || c.status === 'scheduled')
    .map((c) => {
      const s = statsById.get(c.id)
      const sent = Number(s?.sent ?? 0)
      // "of total leads" — the queue total is the fallback for a campaign whose
      // leads were imported before it was created.
      const total = leadTally.get(c.id) ?? Number(s?.total ?? 0)
      return {
        id: c.id,
        name: c.name,
        status: c.status,
        sent,
        total: Math.max(total, sent),
        openRate: rate(campaignOpens.get(c.id)?.size ?? 0, sent),
        clickRate: rate(campaignClicks.get(c.id)?.size ?? 0, sent),
      }
    })
    .sort((a, b) => b.sent - a.sent)

  return {
    windowDays: WINDOW_DAYS,
    series,
    sentTotal,
    wowDelta,
    gauges,
    funnel,
    leadsByCampaign,
    leadsTotal: leads.length,
    topSenders,
    queueByStatus,
    queueTotal,
    unsubs,
    opensByHour: hours,
    peakHours,
    campaigns: campaignRows,
    isEmpty: queue.length === 0 && leads.length === 0 && events.length === 0,
  }
}
